import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { AuthShell, SubmitButton } from "./AuthShell";
import { AvatarFace } from "../../design/AvatarFace";
import { palette } from "../../design/tokens";
import { t } from "../../locale";
import { useAuth } from "../../stores/auth";

/**
 * The signed-in account: what is stored, sign out, and self-serve deletion.
 *
 * plan.md §5 — an account restores a display name and a mark and nothing else,
 * so there is deliberately very little here. Deletion is immediate and
 * irreversible, which is why it sits behind a second press rather than a
 * confirm dialog that a thumb can dismiss by accident.
 */
export const Account = () => {
  const [, navigate] = useLocation();
  const session = useAuth((s) => s.session);
  const loading = useAuth((s) => s.loading);
  const displayName = useAuth((s) => s.displayName);
  const avatarSeed = useAuth((s) => s.avatarSeed);
  const error = useAuth((s) => s.error);
  const signOut = useAuth((s) => s.signOut);
  const deleteAccount = useAuth((s) => s.deleteAccount);

  const [confirming, setConfirming] = useState(false);

  const registered = Boolean(session && !session.user.is_anonymous);

  useEffect(() => {
    if (!loading && !registered) navigate("/auth/sign-in", { replace: true });
  }, [loading, registered, navigate]);

  if (!registered) return null;

  return (
    <AuthShell title={t.auth.account} error={error}>
      <div className="flex items-center gap-3 bg-surface px-3 py-3">
        <AvatarFace seed={avatarSeed ?? session?.user.id ?? ""} size={44} />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-base font-bold">
            {displayName ?? t.auth.noNameSaved}
          </span>
          <span className="truncate text-xs text-ink-dim">
            {session?.user.email}
          </span>
        </div>
      </div>

      <SubmitButton
        label={t.auth.signOut}
        disabled={false}
        onClick={async () => {
          await signOut();
          navigate("/", { replace: true });
        }}
      />

      <div className="mt-6 flex flex-col gap-2">
        <p className="text-xs text-ink-dim">{t.auth.deleteExplains}</p>
        <button
          className="w-full px-4 py-3 text-sm font-bold uppercase tracking-widest"
          style={{
            backgroundColor: confirming ? palette.sideB : "transparent",
            color: confirming ? palette.ink : palette.sideB,
            boxShadow: confirming ? undefined : `inset 0 0 0 1px ${palette.sideB}`,
          }}
          onClick={async () => {
            if (!confirming) {
              setConfirming(true);
              return;
            }
            await deleteAccount();
            if (!useAuth.getState().error) navigate("/", { replace: true });
          }}
        >
          {confirming ? t.auth.deleteConfirm : t.auth.deleteAccount}
        </button>
      </div>
    </AuthShell>
  );
};
